import React, { useEffect, useState } from 'react';

import kycPortalService from '../../services/kycPortalService';

/**
 * Sanctions Declaration step — the signing ceremony.
 *
 * The declaration wording comes from the API (single source of truth shared
 * with the PDF renderer), never hard-coded here. Signing needs typed name +
 * title and the explicit e-consent tick; the server refuses anything less,
 * this UI just mirrors that contract.
 */
const KYCPortalDeclaration = ({ onSigned, readOnly }) => {
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState('');
    const [content, setContent] = useState(null);
    const [declaration, setDeclaration] = useState(null);

    const [signerName, setSignerName] = useState('');
    const [signerTitle, setSignerTitle] = useState('');
    const [country, setCountry] = useState('');
    const [consent, setConsent] = useState(false);
    const [signing, setSigning] = useState(false);
    const [signError, setSignError] = useState('');

    useEffect(() => {
        const run = async () => {
            try {
                const data = await kycPortalService.getDeclaration();
                setContent(data.content);
                setDeclaration(data.declaration);
                setCountry(data.declaration.country_of_incorporation || '');
            } catch (err) {
                if (err.response?.status !== 401) {
                    setLoadError('Could not load the declaration. Please try again.');
                }
            } finally {
                setLoading(false);
            }
        };
        run();
    }, []);

    const handleSign = async () => {
        setSignError('');
        setSigning(true);
        try {
            const data = await kycPortalService.signDeclaration({
                signerName,
                signerTitle,
                consent,
                countryOfIncorporation: country,
            });
            setDeclaration(data.declaration);
            if (onSigned) onSigned();
        } catch (err) {
            setSignError(err.response?.data?.error || 'Signing failed. Please try again.');
        } finally {
            setSigning(false);
        }
    };

    const handleDownload = async () => {
        try {
            const blob = await kycPortalService.downloadDeclaration();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = 'IMT Sanctions Declaration - signed.pdf';
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            setSignError('Download failed. Please try again.');
        }
    };

    if (loading) {
        return (
            <div className="kyc-portal-card">
                <p className="kyc-portal-help">Loading the declaration…</p>
            </div>
        );
    }
    if (loadError || !content || !declaration) {
        return (
            <div className="kyc-portal-card">
                <div className="kyc-portal-error">{loadError || 'Declaration unavailable.'}</div>
            </div>
        );
    }

    const signed = declaration.status === 'signed' || declaration.status === 'signed_manual';
    const needsCountry = !declaration.country_of_incorporation;

    return (
        <div className="kyc-portal-card">
            <h2>{content.title}</h2>
            <p className="kyc-portal-help">
                {content.division} · {content.version_line}
            </p>
            <p className="kyc-portal-help">{content.intro}</p>

            <h3>Definitions</h3>
            <table className="kyc-portal-decl-table">
                <tbody>
                    {content.definitions.map((definition) => (
                        <tr key={definition.term}>
                            <td className="kyc-portal-decl-term">{definition.term}</td>
                            <td>{definition.meaning}</td>
                        </tr>
                    ))}
                </tbody>
            </table>

            <h3>Counterparty</h3>
            <table className="kyc-portal-decl-table">
                <tbody>
                    <tr>
                        <td className="kyc-portal-decl-term">Counterparty full legal name</td>
                        <td>{declaration.counterparty_name || '—'}</td>
                    </tr>
                    <tr>
                        <td className="kyc-portal-decl-term">Country of incorporation</td>
                        <td>
                            {signed || !needsCountry ? (
                                declaration.country_of_incorporation || country || '—'
                            ) : (
                                <input
                                    type="text"
                                    className="kyc-portal-decl-input"
                                    value={country}
                                    onChange={(event) => setCountry(event.target.value)}
                                    placeholder="e.g. Singapore"
                                    disabled={readOnly}
                                />
                            )}
                        </td>
                    </tr>
                    <tr>
                        <td className="kyc-portal-decl-term">IMT counterparty code</td>
                        <td>{declaration.counterparty_code || '—'}</td>
                    </tr>
                </tbody>
            </table>

            <h3>Declaration</h3>
            <p className="kyc-portal-help">{content.representations_lead}</p>
            <ol className="kyc-portal-decl-reps">
                {content.representations.map((representation, index) => (
                    <li key={index}>{representation}</li>
                ))}
            </ol>

            {signed ? (
                <>
                    <div className="kyc-portal-success">
                        {declaration.status === 'signed_manual' ? (
                            <>A manually signed copy of this declaration is on file with IMT.</>
                        ) : (
                            <>
                                Signed by <strong>{declaration.signer_name}</strong>
                                {declaration.signer_title ? ` (${declaration.signer_title})` : ''} on{' '}
                                {declaration.signed_at
                                    ? new Date(declaration.signed_at).toLocaleString()
                                    : ''}.
                                {declaration.seal_status === 'sealed'
                                    ? ' The document carries a cryptographic seal.'
                                    : ''}
                            </>
                        )}
                    </div>
                    {declaration.has_document && (
                        <button
                            type="button"
                            className="kyc-portal-btn-secondary"
                            onClick={handleDownload}
                        >
                            Download signed declaration (PDF)
                        </button>
                    )}
                </>
            ) : readOnly ? (
                <div className="kyc-portal-error">
                    This declaration has not been signed. Please contact IMT to reopen the case.
                </div>
            ) : (
                <div className="kyc-portal-decl-signbox">
                    <h3>Sign this declaration</h3>
                    <div className="kyc-portal-decl-signgrid">
                        <label>
                            Full name of signer
                            <input
                                type="text"
                                className="kyc-portal-decl-input"
                                value={signerName}
                                onChange={(event) => setSignerName(event.target.value)}
                                placeholder="e.g. Jane Doe"
                            />
                        </label>
                        <label>
                            Title / position
                            <input
                                type="text"
                                className="kyc-portal-decl-input"
                                value={signerTitle}
                                onChange={(event) => setSignerTitle(event.target.value)}
                                placeholder="e.g. Director"
                            />
                        </label>
                    </div>
                    <label className="kyc-portal-decl-consent">
                        <input
                            type="checkbox"
                            checked={consent}
                            onChange={(event) => setConsent(event.target.checked)}
                        />
                        <span>{content.consent_text}</span>
                    </label>
                    {signError && <div className="kyc-portal-error">{signError}</div>}
                    <button
                        type="button"
                        className="kyc-portal-btn-success"
                        disabled={
                            signing || !consent || !signerName.trim() || !signerTitle.trim()
                            || (needsCountry && !country.trim())
                        }
                        onClick={handleSign}
                    >
                        {signing ? 'Signing…' : 'Sign this declaration'}
                    </button>
                    <p className="kyc-portal-help kyc-portal-decl-fineprint">
                        Signing records your name, e-mail, IP address and a timestamp in a
                        tamper-evident audit trail, and e-mails you a copy of the signed PDF.
                    </p>
                </div>
            )}
        </div>
    );
};

export default KYCPortalDeclaration;
